/**
 * AWS CloudWatch RUM — per-environment config.
 *
 * Each deploy is built for one environment and injects a single set of
 * `VITE_RUM_*` env vars (sourced from SSM by the CI pipeline).
 */

export interface RumEnvironmentConfig {
  /** CloudWatch RUM app monitor id. */
  applicationId: string;
  /** App monitor version, reported with every event. */
  applicationVersion: string;
  /** AWS region hosting the app monitor, e.g. `ap-southeast-2`. */
  region: string;
  /** Cognito identity pool used for unauthenticated (guest) access. */
  identityPoolId: string;
  /** RUM data plane endpoint for the region. */
  endpoint: string;
}

/**
 * Returns `false` for vitest runs and for local dev unless opted in via
 * `VITE_RUM_FORCE_ENABLE=true`. All other (production) builds return `true`
 * and rely on `getRumConfig()` throwing if the required vars are missing.
 */
export const isRumEnabled = (): boolean => {
  // Never initialise the real SDK under vitest — it opens network sockets
  // and registers global handlers.
  if (import.meta.env.MODE === 'test') return false;

  // Disabled in local dev unless a developer explicitly opts in.
  if (import.meta.env.DEV && import.meta.env.VITE_RUM_FORCE_ENABLE !== 'true') {
    return false;
  }

  return true;
};

/**
 * Builds the RUM client config from build-time env vars.
 *
 * Throws when a required `VITE_RUM_*` var is missing — callers are
 * expected to catch and treat this as "RUM disabled".
 */
export const getRumConfig = (): RumEnvironmentConfig => {
  const region = import.meta.env.VITE_RUM_REGION;
  const applicationId = import.meta.env.VITE_RUM_APP_ID;
  const identityPoolId = import.meta.env.VITE_RUM_IDENTITY_POOL_ID;
  const endpoint = import.meta.env.VITE_RUM_ENDPOINT;

  const missing = (
    [
      ['VITE_RUM_REGION', region],
      ['VITE_RUM_APP_ID', applicationId],
      ['VITE_RUM_IDENTITY_POOL_ID', identityPoolId],
      ['VITE_RUM_ENDPOINT', endpoint],
    ] as const
  )
    .filter(([, value]) => !value)
    .map(([name]) => name);

  if (missing.length > 0) {
    throw new Error(`RUM config incomplete: missing ${missing.join(', ')}`);
  }

  return {
    applicationId: applicationId as string,
    applicationVersion: import.meta.env.VITE_RUM_APP_VERSION || '1.0.0',
    region,
    identityPoolId: identityPoolId as string,
    endpoint: endpoint as string,
  };
};
